<template>
  <div class="file-card">
    <div class="file-header row d-flex align-items-center">
      <div class="col-auto px-0">
        <div class="icon-lg icon-shape d-flex justify-content-center ms-3">
          <i :class="`${icon}`" :style="{ color: color }"></i>
        </div>
      </div>
      
      <div class="col px-0" style="min-width: 0;">
        <div class="d-flex flex-column justify-content-center">
          <p class="file-title text-sm mb-0">{{ title }}</p>
          <p class="file-meta text-xs mb-0">{{ dateTime }} • {{ size }}</p>
        </div>
      </div>
      
      <div class="col-auto d-flex justify-content-center">
        <div class="dropdown">
          <button
            class="btn"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            @click.stop
          >
            <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
          </button>
          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="actionsDropdown">
            <!-- <li>
              <a class="dropdown-item" @click="copyClick">
                <FontAwesomeIcon icon="fa-solid fa-copy" class="me-2" /> Copy Link
              </a>
            </li> -->
            <!-- <li>
              <a class="dropdown-item" @click="downloadClick">
                <FontAwesomeIcon icon="fa-solid fa-download" class="me-2" /> Download
              </a>
            </li> -->
            <li>
              <a class="dropdown-item text-danger" @click="deleteClick">
                <FontAwesomeIcon icon="fa-solid fa-trash" class="me-2" /> Delete
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="row d-flex mt-3 justify-content-between my-3 mx-3">
      <div class="col-auto d-flex align-items-center px-0">
        <button
          class="btn font-weight-light text-capitalize mx-0 my-0"
          aria-label="Download file"
          :style="{ 
            border: '0.5px solid rgb(229, 231, 235)', 
            color: isDownloading ? '#999' : 'black', 
            textDecoration: 'none',
            opacity: isDownloading ? '0.6' : '1',
            cursor: isDownloading ? 'not-allowed' : 'pointer'
          }"
          :disabled="isDownloading"
          @click="handleDownload"
        >
          <FontAwesomeIcon 
            v-if="!isDownloading" 
            icon="fa-solid fa-download" 
            style="font-size: 14px;" 
          />
          <div 
            v-else 
            class="spinner-border spinner-border-sm" 
            style="font-size: 14px; width: 14px; height: 14px;"
            role="status"
          >
            <span class="visually-hidden">Downloading...</span>
          </div>
          &nbsp; {{ isDownloading ? 'Downloading...' : 'Download' }}
        </button>
      </div>
      
      <div class="col-auto d-flex align-items-center px-0">  
        <div class="avatar-group d-flex align-items-center">
          <button
            class="btn font-weight-light text-capitalize mx-0 my-0"
            aria-label="Share file"
            @click="handleShareClick"
          >
            <FontAwesomeIcon icon="fa-solid fa-share-nodes" style="font-size: 14px;" />
          </button>
          
          <div v-if="isLoadingShares" class="avatar avatar-xs rounded-circle bg-light d-flex align-items-center justify-content-center">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <template v-else>
            <a
              v-for="viewer of currentPermittedViewers"
              :key="viewer.email"
              href="javascript:;"
              class="avatar avatar-xs rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="bottom"
              :title="viewer.name"
            >
              <div class="">
                <material-avatar
                  :alt="viewer.name"
                  :color="viewer.color"
                  size="sm"
                  shadow="sm"
                  circular
                />
              </div>
            </a>
          </template>
        </div>
      </div>
    </div>
    
    <div v-if="showProgress" class="row mx-3 my-3">
      <material-progress class="col-11 d-flex align-items-center px-0" color="success" :percentage="progressPercentage" />
      <div v-if="downloadStatus" class="col-12 text-xs text-muted mt-1">
        {{ downloadStatus }}
      </div>
    </div>

    <SelectContactModal v-if="isShareOpen" :file-id="fileId" :permitted-viewers="selectedContacts" @close-modal="closeShareModal" @contact-picked="handleContactPicked" @contacts-picked="handleContactsPicked" />
    <!-- <ShareLinkModal v-if="isLinkOpen" :file-id="fileId" @close-link-modal="closeLinkModal" /> -->
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SelectContactModal from "@/views/contacts/SelectContactModal";
import MaterialAvatar from '@/components/ui/MaterialAvatar';
import MaterialProgress from "@/components/ui/MaterialProgress.vue";
// import ShareLinkModal from "./ShareLinkModal.vue";
import { determineFileIcon, determineFileColor, determineFileType } from "@/utils/files.js";
export default {
  name: 'MyFileCard',
  components: {
    SelectContactModal,
    // ShareLinkModal,
    MaterialAvatar,
    MaterialProgress,
  },
  props: {
    fileId: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    fileType: {
      type: String,
      default: "Unknown"
    },
    size: {
      type: String,
      default: "0kb"
    },
    dateTime: {
      type: String,
      default: ""
    },
    isEncrypted: {
      type: Boolean,
      default: false
    }
  },
  emits: ['file-deleted'],
  data() {
    return {
      isShareOpen: false,
      isLinkOpen: false,
      showProgress: false,
      progressPercentage: 0,
      hasLoadedShares: false,
      isDownloading: false,
      isSharing: false,
      downloadStatus: ''
    };
  },
  computed: {
    ...mapGetters("files", [
      "isLoadingShares",
      "getFileShares"
    ]),
    ...mapGetters("contacts", [
      "getContactList",
      "getContactsByEmails",
      "getContactColor"
    ]),
    ...mapGetters("encryption", ["isEncryptionInitialized"]),
    fileShares() {
      return this.getFileShares(this.fileId) || [];
    },
    currentPermittedViewers() {
      return this.fileShares.map(share => ({
        email: share.email,
        name: share.name,
        color: this.getContactColor(share.email)
      }));
    },
    selectedContacts() {
      return this.getContactsByEmails(this.currentPermittedViewers.map(v => v.email));
    },
    fileIcon() {
      if (this.fileType) {
        return `/icons/${this.fileType}.png`;
      }
      return '/icons/file.png';
    },
    icon() {
      return determineFileIcon(determineFileType(this.title || ''))
    },
    color() {
      return determineFileColor(determineFileType(this.title || ''))
    }
  },
  created() {
    this.loadShares();

  },
  methods: {
    ...mapActions("files", [
      "deleteFileAction", 
      "downloadFileAction", 
      "fetchSharesForFile",
      "shareFilesAction"
    ]),
    ...mapActions('snackbar', ['showSnackbar']),
    ...mapActions("contacts", [
      "fetchContactList"
    ]),

    async loadShares() {
      if(this.getContactList.length === 0) {
        await this.fetchContactList();
      }

      if (this.fileId && !this.isLoadingShares) {
        try {
          await this.fetchSharesForFile(this.fileId);
          this.hasLoadedShares = true;
        } catch (error) {
          console.error('Failed to load shares:', error);
        }
      }
    },
    async handleDownload() {
      if (this.isDownloading) {
        return;
      }
      
      this.isDownloading = true;
      this.showProgress = true;
      this.progressPercentage = 0;
      this.downloadStatus = 'Starting download...';
      
      try {
        await this.downloadFileAction({
          fileId: this.fileId,
          onProgress: (progress, progressInfo) => {
            this.progressPercentage = progress;
            this.downloadStatus = progressInfo?.status || 'Downloading...';
          }
        });
      } catch (error) {
        console.error('Download failed:', error);
        this.showSnackbar({ 
          title: 'Download failed: ' + (error.message || 'Unknown error'), 
          color: 'danger' 
        });
      } finally {
        this.showProgress = false;
        this.progressPercentage = 0;
        this.downloadStatus = '';
        this.isDownloading = false;
      }
    },
    async handleContactPicked(contact) {
      if (!contact.publicKey) {
        this.showSnackbar({
          title: 'Cannot share with unverified contact',
          color: 'danger'
        });
        return;
      }

      if (!this.isEncryptionInitialized) {
        this.showSnackbar({
          title: 'Encryption not initialized. Please try again.',
          color: 'danger'
        });
        return;
      }

      this.isSharing = true;
      try {
        await this.shareFilesAction({
          fileIds: [this.fileId],
          contact: contact
        });
        // this.showSnackbar({ 
        //   title: `File shared successfully with ${contact.name}`, 
        //   color: 'success' 
        // });
        this.isShareOpen = false;
        await this.loadShares();
      } catch (error) {
        console.error('Share failed:', error);
        this.showSnackbar({ 
          title: error.message || 'Failed to share file', 
          color: 'danger' 
        });
      } finally {
        this.isSharing = false;
      }
    },

    async handleContactsPicked(contacts) {
      if (!this.isEncryptionInitialized) {
        this.showSnackbar({
          title: 'Encryption not initialized. Please try again.',
          color: 'danger'
        });
        return;
      }

      this.isSharing = true;
      try {
        // Share with all contacts sequentially to avoid race conditions
        for (let i = 0; i < contacts.length; i++) {
          const contact = contacts[i];
          
          if (!contact.publicKey) {
            this.showSnackbar({
              title: `Cannot share with unverified contact: ${contact.name}`,
              color: 'danger'
            });
            continue;
          }

          await this.shareFilesAction({
            fileIds: [this.fileId],
            contact: contact
          });
          
          // Add a small delay between shares to avoid race conditions
          if (i < contacts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
          }
        }
        
        // this.showSnackbar({ 
        //   title: `File shared successfully with ${contacts.length} contact(s)!`, 
        //   color: 'success' 
        // });
        this.isShareOpen = false;
        await this.loadShares();
      } catch (error) {
        console.error('Share failed:', error);
        this.showSnackbar({ 
          title: error.message || 'Failed to share file', 
          color: 'danger' 
        });
      } finally {
        this.isSharing = false;
      }
    },
    handleShareClick() {
      if (!this.isEncrypted) {
        this.showSnackbar({
          title: 'This file is not encrypted and cannot be shared securely.',
          color: 'danger'
        });
        return;
      }
      this.isShareOpen = true;
    },
    copyClick() {
      this.isLinkOpen = true;
    },
    deleteClick() {
      this.deleteFileAction(this.fileId);
    },
    closeShareModal() {
      this.isShareOpen = false;
      this.loadShares();
    },
    closeLinkModal() {
      this.isLinkOpen = false;
    }
  }
}
</script>

<style scoped>
.file-card {
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  border: 1px solid rgb(229, 231, 235);
  color: #333;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.file-header {
  min-height: 48px;
}

.file-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
}

.file-meta {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-menu {
  z-index: $zindex-dropdown;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.avatar.bg-light {
  width: 32px;
  height: 32px;
}
</style>
